import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';

import {
  Table,
  Tbody,
  Tr,
  Flex,
  Thead,
  Th,
  Text,
} from '@chakra-ui/react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useParams, useHistory } from 'react-router-dom';
import { useTable } from 'react-table';

import { Icon } from 'components';
import {
  actions, ordinations, subTables, displayModal, showPages,
} from 'constants/table';
import useTableStore from 'stores/useTable';
import useUser from 'stores/useUser';

import Actions from './Actions';
import DeleteModal from './Delete';
import TableFooter from './Footer';
import Item from './Item';

function OrderIcon(type) {
  if (type === 'asc') {
    return 'sort-up';
  }
  return 'sort-down';
}

export default function Tab({
  setOrderby,
  toggleOrderType,
  order,
  orderType,
  onPaginate,
  page,
  isFetching,
  refetch,
  fallbackContent = null,
}) {
  const content = fallbackContent ?? useParams<GeneralRoute>().content;
  const [modalData, setModalData] = useState<GenericObject>(null);
  const { push } = useHistory();
  const { user } = useUser();
  const table = useTableStore(useCallback((state) => state.tables[content], [content]));

  const [sentryRef] = useInfiniteScroll({
    loading: isFetching,
    hasNextPage: table?.meta?.lastPage > page,
    onLoadMore: onPaginate,
    rootMargin: '0px 0px 200px 0px',
  });

  useEffect(() => {
    if (modalData && !isFetching) {
      setModalData(table.data.find((item) => item.id === modalData.id));
    }
  }, [table, isFetching]);

  const {
    headerGroups,
    rows,
    prepareRow,
  } = useTable({
    columns: useMemo(() => table.display.filter(
      (item) => (user?.isSuperUser ? item : !item.superUserOnly),
    ), [table.display]),
    data: useMemo(() => table.data, [table.data]),
  });

  const Keys = useCallback(() => (
    <Thead>
      {headerGroups.map((headerGroup) => (
        <Tr
          bg="gray.100"
          key={`header-${headerGroup.id}`}>
          {headerGroup.headers.map((column) => (
            <Th
              p={4}
              key={column.id}>
              <Flex
                _hover={{ cursor: 'pointer' }}
                onClick={() => {
                  setOrderby(ordinations[content][column.id]);
                  toggleOrderType();
                }}>
                <Text color="gray.500">{column.render('Header')}</Text>
                <Icon
                  ml={1}
                  name={column.id === order ? OrderIcon(orderType) : 'sort'}
                  color="gray.400"
                />
              </Flex>
            </Th>
          ))}
          <Th
            hidden={!actions[content]?.onDelete && !actions[content]?.onEdit}
            p={4}>
            <Flex
              mr={6}
              justify="flex-end">
              <Text>Ações</Text>
            </Flex>
          </Th>
        </Tr>
      ))}
    </Thead>
  ), [headerGroups, orderType, order]);

  const Body = useCallback(() => {
    const onShow = {
      show: (row) => push(`/${content}/${row.original.id}`),
      modal: (row) => setModalData(row.original),
      table: (row) => push(`/table/${subTables[content]}/${row.original.id}`),
      page: (row) => {
        push(`${showPages[content]}/${row.original.id}`);
      },
      null: () => {},
    }[actions[content]?.onShow];

    return (
      <Tbody>
        {rows.map((row) => {
          prepareRow(row);
          return (
            <Tr
              _hover={{
                bg: 'gray.150',
              }}
              key={`row-${row.id}`}>
              {row.cells.map((cell) => (
                <Item
                  {...cell.column}
                  key={`row-${row.id}-${JSON.stringify(cell.value)}`}
                  variant="xSmallSemiBold"
                  item={cell.value}
                  original={row.original}
                  onClick={() => onShow(row)}
                  cursor="pointer"
                />
              ))}
              <Th
                hidden={!actions[content]?.onDelete && !actions[content]?.onEdit}
                zIndex={11000}>
                <Flex
                  justify="flex-end">
                  <Actions
                    refetch={refetch}
                    row={row}
                  />
                </Flex>
              </Th>
            </Tr>
          );
        })}
      </Tbody>
    );
  }, [rows]);

  const ContentModal = useMemo<React.FC<any>>(
    () => displayModal[content] ?? React.Fragment,
    [content, modalData, isFetching]);
  return (
    <Flex
      flexDir="column"
      pb={10}>
      <ContentModal
        isOpen={!!modalData}
        refetch={refetch}
        isFetching={isFetching}
        data={modalData}
        onClose={() => setModalData(null)}
      />
      <DeleteModal onSuccess={refetch} />
      <Table
        bg="white"
        variant="simple">
        <Keys />
        {!table.data && isFetching ? <Flex>carregando...</Flex> : <Body />}
      </Table>
      <TableFooter
        onClick={onPaginate}
        isFetching={isFetching}
      />
      <span ref={sentryRef} />
    </Flex>
  );
}
